import cx from "clsx";
import { GalleryCollector } from "kreate-common/modules/business-types";
import ButtonSecondary from "kreate-common/modules/kreate-ui/components/ButtonSecondary";
import DropdownMenu from "kreate-common/modules/kreate-ui/components/DropdownMenu";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { CLIENT_ENV } from "kreate-env/client";
import { httpPost$UpdateGalleryInfo } from "kreate-server/commands/UpdateGalleryInfo/fetcher";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { FaFacebook } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { MdEdit, MdLanguage, MdLink, MdShare } from "react-icons/md";

import Decorator from "../../../PageHome/containers/SectionEventsFromRibbons/components/Decorator";
import { authenticateByCardanoWallet } from "../../../PageLogin/utils";
import GalleryAvatarViewer$WithCustomFrame from "../../../PageUpdateGalleryInfo/components/GalleryAvatarViewer$WithCustomFrame";
import { isFrameKey } from "../../../PageUpdateGalleryInfo/components/GalleryAvatarViewer$WithCustomFrame/config";
import GalleryAvatarViewer$WithoutCustomFrame from "../../../PageUpdateGalleryInfo/components/GalleryAvatarViewer$WithoutCustomFrame";
import ModalAvatarPicker from "../../../PageUpdateGalleryInfo/modals/ModalAvatarPicker";
import { SelectedAvatar } from "../../../PageUpdateGalleryInfo/types";
import { extractUrl } from "../../../PageUpdateProfile/fields/FieldSocialLinkItem/utils";
import ButtonIcon from "../../components/ButtonIcon";

import DetailedAddressViewer from "./components/DetailedAddressViewer";
import styles from "./index.module.scss";
import { getPageTitle } from "./utils";

import { useCardanoWalletApi } from "@/modules/cardano-wallet";
import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import {
  shareOnFacebook,
  shareOnTwitter,
} from "@/modules/common-utils/shareOn";
import { getShortenedAddress } from "@/modules/kreate-components/components/AddressViewer/utils";
import AvatarViewer from "@/modules/kreate-components/components/AvatarViewer";
import LongTextViewer from "@/modules/kreate-components/components/LongTextViewer";
import UserDisplayNameViewerV2 from "@/modules/kreate-components/components/UserDisplayNameViewerV2";
import { useToast } from "@/modules/kreate-contexts/ToastContext";
import {
  useModalTrigger$ModalAsyncGenerator,
  waitModal,
} from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import {
  getLinkToPageGalleryCollector,
  getLinkToPageGalleryCollector$Edit,
  getLinkToPageProfile,
} from "@/modules/navigation/utils/getLinkTo";
import { CHANNEL_KEY_TO_CHANNEL_INFO } from "@/modules/social-links/config";

type AddressIsh = string;

type Props = {
  className?: string;
  style?: React.CSSProperties;
  galleryCollector: GalleryCollector | null | undefined;
  addressIsh: AddressIsh;
};

export default function SectionHeadline({
  className,
  style,
  galleryCollector,
  addressIsh,
}: Props) {
  const walletApi = useCardanoWalletApi();
  const router = useRouter();
  const { showMessage } = useToast();
  const galleryInfo = galleryCollector?.populated?.galleryInfo;
  const loginStatus = useLoginStatus();

  const modalTriggers = {
    ModalAsyncGenerator: useModalTrigger$ModalAsyncGenerator(),
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    showMessage({
      color: "success",
      title: "Copied to Clipboard",
    });
  };

  const isGalleryOwner =
    loginStatus?.loggedIn &&
    loginStatus.userId === galleryCollector?.populated?.userProfile?.userId;

  const isGalleryOwnerWalletConnected =
    walletApi.type === "ready" &&
    galleryCollector?.populated?.userProfile?.walletAddresses?.includes(
      walletApi.address
    );

  const defaultTitle = addressIsh.startsWith("$")
    ? addressIsh
    : getShortenedAddress(addressIsh, { prefixLength: 8, suffixLength: 8 });

  const handleEditAvatar = async () => {
    await modalTriggers.ModalAsyncGenerator.openModal(async function* () {
      const newSelectedAvatar = yield* waitModal<
        SelectedAvatar | null | undefined
      >((resolve, reject) => (
        <ModalAvatarPicker
          open
          addressIsh={addressIsh}
          initialSelectedAvatar={
            galleryInfo?.avatar && galleryInfo?.avatarAssetId
              ? {
                  avatar: galleryInfo?.avatar,
                  assetId: galleryInfo?.avatarAssetId,
                }
              : undefined
          }
          onSubmit={resolve}
          onCancel={() => reject(null)}
        />
      ));
      yield null;
      await httpPost$UpdateGalleryInfo("/api/v1/me/update-gallery-info", {
        avatarAssetId: newSelectedAvatar ? newSelectedAvatar.assetId : null,
      });
      showMessage({
        color: "success",
        title: "Avatar set successfully!",
      });
      await shamelesslyRevalidateEverything();
    });
  };

  const galleryInfo_customFrame = galleryInfo?.customFrame;

  return (
    <section className={cx(styles.container, className)} style={style}>
      <Flex.Col alignItems="center" gap="16px">
        <div style={{ position: "relative" }}>
          {!galleryInfo?.avatar &&
          !isGalleryOwner ? undefined : galleryInfo_customFrame &&
            isFrameKey(galleryInfo_customFrame) ? (
            <GalleryAvatarViewer$WithCustomFrame
              style={
                // adhoc glow effect
                galleryInfo_customFrame === "frame1420"
                  ? { boxShadow: "0px 16px 32px 0px #CE973729" }
                  : undefined
              }
              avatar={galleryInfo.avatar}
              size="160px"
              frame={galleryInfo_customFrame}
            />
          ) : (
            <GalleryAvatarViewer$WithoutCustomFrame
              avatar={galleryInfo?.avatar}
              size="88px"
              border
            />
          )}
          {isGalleryOwner ? (
            <ButtonIcon
              size="32px"
              icon={<MdEdit size="24px" />}
              className={styles.buttonEditAvatar}
              onClick={handleEditAvatar}
            />
          ) : null}
        </div>
        <h1 className={styles.title}>{galleryInfo?.title || defaultTitle}</h1>
        <Flex.Col alignItems="center" gap="8px">
          {galleryCollector?.addresses.map(({ address, adaHandles }, index) => (
            <DetailedAddressViewer
              key={index}
              address={address}
              adaHandles={adaHandles}
            />
          ))}
        </Flex.Col>
        {galleryInfo?.description ? (
          <LongTextViewer.Collapsible
            className={styles.description}
            value={galleryInfo.description}
            unstyled
          />
        ) : undefined}
        <Flex.Row
          gap="16px"
          flexWrap="wrap"
          alignItems="center"
          paddingTop="8px"
          justifyContent="center"
          maxWidth="100%"
        >
          <Flex.Row
            gap="12px"
            alignItems="center"
            flexWrap="wrap"
            justifyContent="center"
            minWidth="0"
          >
            {galleryCollector?.populated?.userProfile &&
            galleryCollector.isCollectorAnArtist ? (
              <ButtonSecondary
                className={styles.buttonUserProfile}
                size="lg"
                icon={
                  <AvatarViewer
                    value={galleryCollector.populated.userProfile?.avatar}
                    size="32px"
                  />
                }
                onClick={async () => {
                  galleryCollector?.populated?.userProfile &&
                    (await router.push(
                      getLinkToPageProfile(
                        galleryCollector.populated.userProfile
                      )
                    ));
                }}
              >
                <UserDisplayNameViewerV2
                  style={{ textTransform: "none", minWidth: "0" }}
                  as="span"
                  user={galleryCollector.populated.userProfile}
                  openInNewTab={false}
                  unstyled
                />
              </ButtonSecondary>
            ) : undefined}
            {isGalleryOwner || isGalleryOwnerWalletConnected ? (
              <ButtonSecondary
                icon={<MdEdit />}
                size="lg"
                content="Edit Gallery"
                onClick={async () => {
                  if (isGalleryOwnerWalletConnected && !isGalleryOwner) {
                    const { userId } = await authenticateByCardanoWallet(
                      walletApi.cip30Wallet
                    );
                    showMessage({
                      color: "success",
                      title: "Logged in successfully!",
                    });
                    console.log(`logged in as ${userId}`);
                    await shamelesslyRevalidateEverything();
                  }
                  await router.push(
                    getLinkToPageGalleryCollector$Edit(addressIsh)
                  );
                }}
              />
            ) : undefined}
            <DropdownMenu.Menu
              trigger={<ButtonSecondary icon={<MdShare />} size="lg-square" />}
            >
              <DropdownMenu.Item
                icon={<FaXTwitter />}
                content="Twitter"
                onSelect={() =>
                  shareOnTwitter(
                    getLinkToPageGalleryCollector(addressIsh),
                    getPageTitle({ galleryInfo, defaultTitle })
                  )
                }
              />
              <DropdownMenu.Item
                icon={<FaFacebook />}
                content="Facebook"
                onSelect={() =>
                  shareOnFacebook(getLinkToPageGalleryCollector(addressIsh))
                }
              />
              <DropdownMenu.Item
                icon={<MdLink />}
                content="Copy Link"
                onSelect={() =>
                  copyToClipboard(
                    CLIENT_ENV.KREATE_ORIGIN +
                      getLinkToPageGalleryCollector(addressIsh)
                  )
                }
              />
            </DropdownMenu.Menu>
          </Flex.Row>
          {galleryInfo?.socialLinks?.length ? (
            <Decorator.VerticalLine
              size="32px"
              variant="dashed"
              opacity="16%"
            />
          ) : undefined}
          {galleryInfo?.socialLinks?.length ? (
            <Flex.Row gap="12px" alignItems="center">
              {galleryInfo?.socialLinks.map(
                // TODO: @kien-kreate: extract this component for reusability
                (href, index) => {
                  const { host } = extractUrl(href);
                  const channelInfo = Object.values(
                    CHANNEL_KEY_TO_CHANNEL_INFO
                  ).find((channelInfo) =>
                    channelInfo.acceptedDomains.some(
                      (d) => d === host || host.endsWith("." + d)
                    )
                  );
                  return (
                    <Link
                      key={index}
                      href={href}
                      className={styles.iconContainer}
                      rel="noreferer"
                      target="_blank"
                    >
                      {channelInfo?.icon || <MdLanguage />}
                    </Link>
                  );
                }
              )}
            </Flex.Row>
          ) : undefined}
        </Flex.Row>
      </Flex.Col>
    </section>
  );
}
